/**
 * Unicode text → LaTeX.
 *
 * Greek letters, operators, super- and subscripts, `\mathbb`-style letters, accented
 * letters, quotes and dashes become the LaTeX that produces them. Characters that only
 * work in maths mode are gathered into `$…$` runs, together with the plain numbers and
 * variables beside them, so "x² + α ≤ 5" becomes one formula and not three.
 */

import {
  ALPHABET_REVERSE,
  type AlphabetStyle,
  MATH_ACCENT_PACKAGE,
  MATH_ACCENT_REVERSE,
  MATH_PREFERRED,
  REVERSE_SYMBOLS,
  type ReverseSymbol,
  STYLE_REVERSE_COMMAND,
  SUBSCRIPT_REVERSE,
  SUPERSCRIPT_REVERSE,
  T1_ACCENTS,
  TEXT_ACCENT_REVERSE,
  VULGAR_REVERSE,
} from "@/lib/tool-kit/latex-dev/unicode-maps";

export interface ToLatexOptions {
  /** Put maths characters inside `$…$`. */
  wrapMath: boolean;
  /** Write quotes and dashes as ligatures (`` '' -- ---) instead of named commands. */
  ligatures: boolean;
  /** Escape `# $ % & _ { } ~ ^ \` so they print literally. */
  escapeSpecials: boolean;
}

export const DEFAULT_TO_LATEX_OPTIONS: ToLatexOptions = {
  wrapMath: true,
  ligatures: true,
  escapeSpecials: true,
};

export interface ToLatexResult {
  latex: string;
  /** Preamble lines the output needs (`\usepackage{amssymb}` …). */
  packages: string[];
  /** Characters with no LaTeX command, left as typed. */
  unmapped: string[];
  /** Characters replaced by a command. */
  converted: number;
}

type Kind = "math" | "text" | "neutral";

interface Token {
  kind: Kind;
  /** The source characters, for run detection. */
  src: string;
  /** Rendering inside maths mode. */
  math: string;
  /** Rendering inside text mode. */
  text: string;
  /** A binary operator, relation or arrow. */
  infix?: boolean;
  mathPkg?: string;
  textPkg?: string;
  /** A superscript or subscript group. */
  script?: boolean;
  /** The Unicode accent this token is, so adjacent overlines can merge. */
  overline?: { mark: string; inner: string };
}

const RE_ASCII_ALNUM = /[A-Za-z0-9]/;
const RE_ASCII_ALNUM_ONLY = /^[A-Za-z0-9]$/;
const RE_TWO_LETTERS = /[A-Za-z]{2}/;
const RE_MARK = /^\p{M}$/u;
const RE_ASCII_LETTER = /^[A-Za-z]$/;
const RE_OPERAND_TAIL =
  /(?:^|[^A-Za-z0-9.])([0-9]+(?:\.[0-9]+)?|[A-Za-z](?:\([^()]*\))?)$/;
const RE_OPERAND_HEAD =
  /^(?:[0-9]+(?:\.[0-9]+)?|[A-Za-z](?![A-Za-z])(?:\([^()]*\))?)/;
const RE_OPERATOR_HEAD = /^ ?[=+\-*/<>] ?/;
const RE_LETTER_START = /^[A-Za-z]/;
const RE_CONTROL_WORD_END = /\\[A-Za-z]+$/;

const SPECIAL_ESCAPES: Readonly<Record<string, string>> = {
  "#": "\\#",
  $: "\\$",
  "%": "\\%",
  "&": "\\&",
  _: "\\_",
  "{": "\\{",
  "}": "\\}",
  "~": "\\textasciitilde{}",
  "^": "\\textasciicircum{}",
  "\\": "\\textbackslash{}",
};

/** Quotes and dashes written as LaTeX ligatures. */
const LIGATURES: Readonly<Record<string, string>> = {
  "“": "``",
  "”": "''",
  "‘": "`",
  "’": "'",
  "–": "--",
  "—": "---",
};

/** Greek letters that have a Unicode superscript or subscript form, back to commands. */
function scriptCharToLatex(ch: string): string {
  const info = REVERSE_SYMBOLS.get(ch);
  return info?.math ? `${info.math.command} ` : ch;
}

function escapeAscii(ch: string, enabled: boolean): string {
  if (enabled && Object.hasOwn(SPECIAL_ESCAPES, ch)) {
    return SPECIAL_ESCAPES[ch];
  }
  return ch;
}

/** ASCII characters that print wrongly in text mode with the default font encoding. */
const TEXT_ONLY_ESCAPES: Readonly<Record<string, string>> = {
  "<": "\\textless{}",
  ">": "\\textgreater{}",
  "|": "\\textbar{}",
};

/** Spaces with a LaTeX spelling. They count as spaces when gathering formulas. */
const SPACE_TOKENS: Readonly<Record<string, string>> = {
  "\u00A0": "~",
  "\u2009": "\\,",
  "\u202F": "\\,",
  "\u2003": "\\quad{}",
};

function neutral(ch: string, opts: ToLatexOptions): Token {
  const rendered = escapeAscii(ch, opts.escapeSpecials);
  const text =
    opts.escapeSpecials && Object.hasOwn(TEXT_ONLY_ESCAPES, ch)
      ? TEXT_ONLY_ESCAPES[ch]
      : rendered;
  return { kind: "neutral", src: ch, math: rendered, text };
}

// ---------------------------------------------------------------------------
// Tokenising
// ---------------------------------------------------------------------------

interface Cluster {
  /** The base character with the combining marks that follow it. */
  chars: string[];
}

function clusters(text: string): Cluster[] {
  const out: Cluster[] = [];
  for (const ch of text) {
    const last = out.at(-1);
    if (last && RE_MARK.test(ch)) {
      last.chars.push(ch);
    } else {
      out.push({ chars: [ch] });
    }
  }
  return out;
}

function scriptToken(
  chars: string[],
  table: ReadonlyMap<string, string>,
  mark: "^" | "_"
): Token {
  const inner = chars
    .map((ch) => scriptCharToLatex(table.get(ch) ?? ch))
    .join("")
    .trimEnd();
  // One plain character needs no braces: x^2, H_2O.
  const rendered = RE_ASCII_ALNUM_ONLY.test(inner)
    ? `${mark}${inner}`
    : `${mark}{${inner}}`;
  return {
    kind: "math",
    src: chars.join(""),
    math: rendered,
    text: rendered,
    script: true,
  };
}

function alphabetToken(style: AlphabetStyle, letters: string): Token {
  const { command, pkg } = STYLE_REVERSE_COMMAND[style];
  const rendered = command === "" ? letters : `\\${command}{${letters}}`;
  return {
    kind: "math",
    src: letters,
    math: rendered,
    text: rendered,
    mathPkg: pkg || undefined,
  };
}

function symbolToken(
  ch: string,
  info: ReverseSymbol,
  opts: ToLatexOptions
): Token {
  const ligature = LIGATURES[ch];
  if (ligature !== undefined && opts.ligatures) {
    return { kind: "text", src: ch, math: ligature, text: ligature };
  }
  if (ch === "−") {
    return { kind: "math", src: ch, math: "-", text: "-", infix: true };
  }
  const { math, text } = info;
  const prefersMath = math && (!text || MATH_PREFERRED.has(ch));
  const kind: Kind = prefersMath ? "math" : "text";
  return {
    kind,
    src: ch,
    math: math ? math.command : `\\mbox{${text?.command ?? ch}{}}`,
    text: text ? `${text.command}{}` : (math?.command ?? ch),
    infix: info.infix,
    mathPkg: math && math.pkg !== "latex" ? math.pkg : undefined,
    textPkg:
      text && text.pkg !== "latex" && text.pkg !== "textcomp"
        ? text.pkg
        : undefined,
  };
}

function accentPackage(mark: string): string | undefined {
  const command = MATH_ACCENT_REVERSE.get(mark);
  return command ? MATH_ACCENT_PACKAGE[command] : undefined;
}

const BELOW_MARKS: ReadonlySet<string> = new Set([
  "\u0323",
  "\u0327",
  "\u0328",
  "\u0331",
]);

function textBase(base: string, marks: string[]): string {
  if (base === "ı") {
    return "\\i";
  }
  if (base === "ȷ") {
    return "\\j";
  }
  const dotted = base === "i" || base === "j";
  // A mark above replaces the dot, so the dotless \i is written; a mark below keeps it.
  return dotted && !marks.some((m) => BELOW_MARKS.has(m)) ? `\\${base}` : base;
}

/** `\'{e}`, `\c{c}`, `\"{\i}` … for a base letter and its marks. */
function textAccentCommand(base: string, marks: string[]): string | null {
  let current = textBase(base, marks);
  for (const mark of marks) {
    const command = TEXT_ACCENT_REVERSE.get(mark);
    if (command === undefined) {
      return null;
    }
    current = `\\${command}{${current}}`;
  }
  return current;
}

function mathBase(base: string): string {
  if (base === "ı") {
    return "\\imath";
  }
  return base === "ȷ" ? "\\jmath" : base;
}

function mathAccentCommand(base: string, marks: string[]): string | null {
  let current = mathBase(base);
  for (const mark of marks) {
    const command = MATH_ACCENT_REVERSE.get(mark);
    if (command === undefined) {
      return null;
    }
    current = `\\${command}{${current}}`;
  }
  return current;
}

function accentToken(cluster: Cluster, composed: string): Token | null {
  const decomposed = Array.from(cluster.chars.join("").normalize("NFD"));
  const [base, ...marks] = decomposed;
  const letter = RE_ASCII_LETTER.test(base) || base === "ı" || base === "ȷ";
  if (!letter || marks.length === 0 || !marks.every((m) => RE_MARK.test(m))) {
    return null;
  }
  const textForm = textAccentCommand(base, marks);
  const mathForm = mathAccentCommand(base, marks);
  if (textForm === null && mathForm === null) {
    return null;
  }
  const precomposed = Array.from(composed).length === 1;
  const kind: Kind = precomposed && textForm !== null ? "text" : "math";
  const needsT1 = marks.some((m) =>
    T1_ACCENTS.has(TEXT_ACCENT_REVERSE.get(m) ?? "")
  );
  const single = marks.length === 1 ? marks[0] : null;
  const token: Token = {
    kind,
    src: composed,
    math: mathForm ?? `\\mbox{${textForm}}`,
    text: textForm ?? mathForm ?? composed,
    textPkg: needsT1 ? "T1" : undefined,
    mathPkg: marks.map(accentPackage).find(Boolean),
  };
  if (single === "̅" || single === "̲") {
    token.overline = { mark: single, inner: base };
  }
  return token;
}

function mergeOverlines(prev: Token, next: Token): boolean {
  if (!(prev.overline && next.overline)) {
    return false;
  }
  if (prev.overline.mark !== next.overline.mark) {
    return false;
  }
  const command = MATH_ACCENT_REVERSE.get(prev.overline.mark);
  const inner = prev.overline.inner + next.overline.inner;
  prev.overline = { mark: prev.overline.mark, inner };
  prev.math = `\\${command}{${inner}}`;
  prev.text = prev.math;
  prev.src += next.src;
  return true;
}

function singleToken(
  cluster: Cluster,
  opts: ToLatexOptions
): { token: Token; counted: boolean; unmapped?: string } {
  const composed = cluster.chars.join("").normalize("NFC");
  const chars = Array.from(composed);
  if (chars.length === 1) {
    const ch = composed;
    if ((ch.codePointAt(0) ?? 0) < 128) {
      return { token: neutral(ch, opts), counted: false };
    }
    const spaced = SPACE_TOKENS[ch];
    if (spaced !== undefined) {
      return {
        token: { kind: "neutral", src: " ", math: spaced, text: spaced },
        counted: true,
      };
    }
    const vulgar = VULGAR_REVERSE.get(ch);
    if (vulgar) {
      const rendered = `\\frac{${vulgar[0]}}{${vulgar[1]}}`;
      return {
        token: { kind: "math", src: ch, math: rendered, text: rendered },
        counted: true,
      };
    }
    const info = REVERSE_SYMBOLS.get(ch);
    if (info) {
      return { token: symbolToken(ch, info, opts), counted: true };
    }
  }
  const accent = accentToken(cluster, composed);
  if (accent) {
    return { token: accent, counted: true };
  }
  return {
    token: { kind: "text", src: composed, math: composed, text: composed },
    counted: false,
    unmapped: composed,
  };
}

interface Tokenised {
  tokens: Token[];
  unmapped: Set<string>;
  converted: number;
}

function isSingle(group: Cluster | undefined): group is Cluster {
  return group !== undefined && group.chars.length === 1;
}

/** The run of raised (or lowered) characters starting at `from`, and where it ends. */
function scriptRun(
  groups: Cluster[],
  from: number
): { token: Token; next: number; count: number } | null {
  const first = groups[from].chars[0];
  const raised =
    groups[from].chars.length === 1 && SUPERSCRIPT_REVERSE.has(first);
  const lowered =
    groups[from].chars.length === 1 && SUBSCRIPT_REVERSE.has(first);
  if (!(raised || lowered)) {
    return null;
  }
  const table = raised ? SUPERSCRIPT_REVERSE : SUBSCRIPT_REVERSE;
  const run: string[] = [];
  let i = from;
  while (isSingle(groups[i]) && table.has(groups[i].chars[0])) {
    run.push(groups[i].chars[0]);
    i++;
  }
  return {
    token: scriptToken(run, table, raised ? "^" : "_"),
    next: i,
    count: run.length,
  };
}

/** The run of one mathematical alphabet starting at `from`, and where it ends. */
function alphabetRun(
  groups: Cluster[],
  from: number
): { token: Token; next: number; count: number } | null {
  const first = groups[from].chars[0];
  const alphabet = ALPHABET_REVERSE.get(first);
  if (!alphabet || REVERSE_SYMBOLS.has(first)) {
    return null;
  }
  const run: string[] = [];
  let i = from;
  while (i < groups.length) {
    const ch = groups[i].chars[0];
    const next = ALPHABET_REVERSE.get(ch);
    if (!next || next.style !== alphabet.style || REVERSE_SYMBOLS.has(ch)) {
      break;
    }
    run.push(next.ch);
    i++;
  }
  return {
    token: alphabetToken(alphabet.style, run.join("")),
    next: i,
    count: run.length,
  };
}

function tokenise(text: string, opts: ToLatexOptions): Tokenised {
  const tokens: Token[] = [];
  const unmapped = new Set<string>();
  let converted = 0;
  const groups = clusters(text.normalize("NFC"));
  let i = 0;
  while (i < groups.length) {
    const run = scriptRun(groups, i) ?? alphabetRun(groups, i);
    if (run) {
      tokens.push(run.token);
      converted += run.count;
      i = run.next;
      continue;
    }
    const made = singleToken(groups[i], opts);
    const previous = tokens.at(-1);
    if (!(previous && mergeOverlines(previous, made.token))) {
      tokens.push(made.token);
    }
    converted += made.counted ? 1 : 0;
    if (made.unmapped !== undefined) {
      unmapped.add(made.unmapped);
    }
    i++;
  }
  return { tokens, unmapped, converted };
}

// ---------------------------------------------------------------------------
// Gathering maths runs
// ---------------------------------------------------------------------------

function isNeutral(token: Token | undefined): boolean {
  return token !== undefined && token.kind === "neutral";
}

function srcAt(tokens: Token[], from: number, to: number): string {
  let out = "";
  for (let i = from; i < to; i++) {
    out += tokens[i].src;
  }
  return out;
}

/** True when the neutral tokens in [from, to) can sit inside one formula. */
function gapIsMath(tokens: Token[], from: number, to: number): boolean {
  const gap = srcAt(tokens, from, to);
  return !(RE_TWO_LETTERS.test(gap) || gap.includes("\n\n"));
}

/** The index of the last token of the formula that starts at `start`. */
function coreEnd(tokens: Token[], start: number): number {
  let end = start;
  let j = start + 1;
  while (j < tokens.length) {
    if (tokens[j].kind === "math") {
      end = j;
      j++;
    } else if (isNeutral(tokens[j])) {
      let k = j;
      while (isNeutral(tokens[k])) {
        k++;
      }
      if (tokens[k]?.kind === "math" && gapIsMath(tokens, j, k)) {
        end = k;
        j = k + 1;
      } else {
        break;
      }
    } else {
      break;
    }
  }
  return end;
}

function neutralTail(tokens: Token[], from: number): string {
  let out = "";
  for (let i = from; i < tokens.length && isNeutral(tokens[i]); i++) {
    out += tokens[i].src;
  }
  return out;
}

/** Take "= 4" or "+ x" after a formula; the operand is a number, a letter or f(x). */
function extendOperation(tokens: Token[], end: number): number {
  let next = end;
  for (;;) {
    const tail = neutralTail(tokens, next + 1);
    const operator = RE_OPERATOR_HEAD.exec(tail);
    const operand = operator
      ? RE_OPERAND_HEAD.exec(tail.slice(operator[0].length))
      : null;
    if (!(operator && operand)) {
      return next;
    }
    next += operator[0].length + operand[0].length;
  }
}

/**
 * Take one operand after a symbol: "5 × 3" wants the 3, and the ∀x of "∀x ∃y" wants its
 * x. Only an infix symbol may be followed by a space; a script already carries its base.
 */
function extendOperand(tokens: Token[], end: number): number {
  const last = tokens[end];
  if (last.script) {
    return end;
  }
  const tail = neutralTail(tokens, end + 1);
  const trimmed = last.infix ? tail.trimStart() : tail;
  const operand = RE_OPERAND_HEAD.exec(trimmed);
  if (!operand) {
    return end;
  }
  return end + (tail.length - trimmed.length) + operand[0].length;
}

/** The index of the first token of the formula, taking an operand before an infix symbol. */
function coreStart(tokens: Token[], start: number, floor: number): number {
  let from = start;
  // A word glued to the formula: the x of x², the 2 of 2x².
  while (
    from > floor &&
    isNeutral(tokens[from - 1]) &&
    RE_ASCII_ALNUM.test(tokens[from - 1].src)
  ) {
    from--;
  }
  if (from !== start || !tokens[start].infix) {
    return from;
  }
  let p = start;
  while (p > floor && isNeutral(tokens[p - 1]) && tokens[p - 1].src === " ") {
    p--;
  }
  const operand = RE_OPERAND_TAIL.exec(srcAt(tokens, floor, p));
  return operand ? p - operand[1].length : start;
}

const RE_SCRIPT_BASE = /^(?:[0-9]*[A-Za-z]|[0-9]+)$/;

/**
 * Where the formula starts. A lone script keeps its base only when the base is a
 * variable or a number (x², 10⁻³): "H₂O" and "km²" are words with a raised or lowered
 * character in them, so only the script goes inside the dollars.
 */
function formulaStart(
  tokens: Token[],
  start: number,
  core: number,
  floor: number
): number {
  const from = coreStart(tokens, start, floor);
  const onlyScripts = tokens.slice(start, core + 1).every((t) => t.script);
  if (!onlyScripts || from === start) {
    return from;
  }
  const followedByLetter =
    isNeutral(tokens[core + 1]) && RE_ASCII_LETTER.test(tokens[core + 1].src);
  const base = srcAt(tokens, from, start);
  return followedByLetter || !RE_SCRIPT_BASE.test(base) ? start : from;
}

function countChar(
  tokens: Token[],
  from: number,
  to: number,
  ch: string
): number {
  let n = 0;
  for (let i = from; i <= to; i++) {
    if (isNeutral(tokens[i]) && tokens[i].src === ch) {
      n++;
    }
  }
  return n;
}

/** Pull a bracket that the formula opens, or closes, into the formula. */
function balanceBrackets(
  tokens: Token[],
  start: number,
  end: number,
  floor: number
): [number, number] {
  let from = start;
  let to = end;
  for (const [open, close] of [
    ["(", ")"],
    ["[", "]"],
  ]) {
    while (
      countChar(tokens, from, to, open) > countChar(tokens, from, to, close) &&
      isNeutral(tokens[to + 1]) &&
      tokens[to + 1].src === close
    ) {
      to++;
    }
    while (
      countChar(tokens, from, to, close) > countChar(tokens, from, to, open) &&
      from > floor &&
      isNeutral(tokens[from - 1]) &&
      tokens[from - 1].src === open
    ) {
      from--;
    }
  }
  return [from, to];
}

function markMath(tokens: Token[]): boolean[] {
  const inMath = new Array<boolean>(tokens.length).fill(false);
  let floor = 0;
  let i = 0;
  while (i < tokens.length) {
    if (tokens[i].kind !== "math") {
      i++;
      continue;
    }
    const core = coreEnd(tokens, i);
    const reach = extendOperation(tokens, extendOperand(tokens, core));
    const [start, end] = balanceBrackets(
      tokens,
      formulaStart(tokens, i, core, floor),
      reach,
      floor
    );
    for (let k = start; k <= end; k++) {
      inMath[k] = true;
    }
    floor = end + 1;
    i = end + 1;
  }
  return inMath;
}

// ---------------------------------------------------------------------------
// Output
// ---------------------------------------------------------------------------

function join(pieces: string[]): string {
  const out: string[] = [];
  let previous = "";
  for (const piece of pieces) {
    // A control word ends at the piece boundary, so only the last piece needs testing.
    if (RE_CONTROL_WORD_END.test(previous) && RE_LETTER_START.test(piece)) {
      out.push(" ");
    }
    out.push(piece);
    previous = piece;
  }
  return out.join("");
}

function collectPackage(packages: Set<string>, pkg: string | undefined): void {
  if (pkg) {
    packages.add(pkg);
  }
}

const PACKAGE_LINES: Readonly<Record<string, string>> = {
  T1: "\\usepackage[T1]{fontenc}",
  fontenc: "\\usepackage[T1]{fontenc}",
};

function packageLine(pkg: string): string {
  return PACKAGE_LINES[pkg] ?? `\\usepackage{${pkg}}`;
}

function render(
  tokens: Token[],
  inMath: boolean[],
  opts: ToLatexOptions,
  packages: Set<string>
): string {
  const out: string[] = [];
  let i = 0;
  while (i < tokens.length) {
    if (opts.wrapMath && inMath[i]) {
      const pieces: string[] = [];
      while (i < tokens.length && inMath[i]) {
        pieces.push(tokens[i].math);
        collectPackage(packages, tokens[i].mathPkg);
        i++;
      }
      out.push(`$${join(pieces).trim()}$`);
      continue;
    }
    const token = tokens[i];
    const asMath = !opts.wrapMath && token.kind === "math";
    out.push(asMath ? token.math : token.text);
    collectPackage(packages, asMath ? token.mathPkg : token.textPkg);
    i++;
  }
  return join(out);
}

/** Convert Unicode text to LaTeX. Pure and synchronous. */
export function unicodeToLatex(
  source: string,
  options: ToLatexOptions = DEFAULT_TO_LATEX_OPTIONS
): ToLatexResult {
  const { tokens, unmapped, converted } = tokenise(source, options);
  const packages = new Set<string>();
  const inMath = options.wrapMath ? markMath(tokens) : tokens.map(() => false);
  const latex = render(tokens, inMath, options, packages);
  return {
    latex,
    packages: [...new Set([...packages].map(packageLine))],
    unmapped: [...unmapped],
    converted,
  };
}
